import { createPortal } from 'react-dom'
import { useEffect, useRef, useState } from 'react'
import type { GeoJSONSource, MapMouseEvent } from 'maplibre-gl'
import { useAlertStore, selectFilteredAlerts } from '../../store/alertStore'
import { useAlertGeoJSON, useMapAlerts } from '../../hooks/useAlerts'
import { useMapInstance } from '../../hooks/useMap'
import { loadMapIcons } from '../../utils/mapIcons'
import type { AlertSummary } from '../../types/cap'

const SOURCE_ID = 'cap-alerts'
const ICON_LAYER = 'cap-icons'
const ICON_RING = 'cap-icon-ring'
const SELECTED_SOURCE = 'cap-selected'
const SELECTED_FILL = 'cap-selected-fill'
const SELECTED_OUTLINE = 'cap-selected-outline'

// Shrinks icons further as the map zooms out, on top of the existing priority scaling below —
// at country-wide zoom a dense fire season no longer renders as a solid mass of same-size icons.
// MapLibre's style spec only allows a ["zoom"] expression as the sole top-level interpolate/step
// input — it can't be composed with e.g. ["*", ...] — so zoom must be the outer interpolate, with
// a priority-based interpolate as each stop's output (the standard "zoom-and-property" pattern).
const ZOOM_SIZE_STOPS: [zoom: number, factor: number][] = [[3, 0.35], [5, 0.55], [8, 0.85], [11, 1]]

function zoomScaledPrioritySize(min: number, max: number) {
  return [
    'interpolate', ['linear'], ['zoom'],
    ...ZOOM_SIZE_STOPS.flatMap(([zoom, factor]) => [
      zoom,
      ['interpolate', ['linear'], ['get', 'priority_score'], 1, min * factor, 5, max * factor],
    ]),
  ]
}

const NORMAL_SIZE = zoomScaledPrioritySize(0.45, 0.9)
const selectedSizeExpr = (id: string) => [
  'case', ['==', ['get', 'id'], id],
  zoomScaledPrioritySize(0.8, 1.4),
  NORMAL_SIZE,
]

// Lower sort-key wins placement when icons collide (icon-allow-overlap is off below) — so higher
// priority always wins over lower, and the selected alert always wins over everything.
const sortKeyExpr = (selectedId: string | null) => [
  'case', ['==', ['get', 'id'], selectedId ?? ''],
  -100,
  ['-', 0, ['get', 'priority_score']],
]

function buildCentroidGeoJSON(alerts: AlertSummary[]) {
  return {
    type: 'FeatureCollection' as const,
    features: alerts
      .filter((a) => a.centroid)
      .map((a) => ({
        type: 'Feature' as const,
        id: a.id,
        geometry: a.centroid!,
        properties: {
          id: a.id,
          hazard_type: a.hazard_type,
          priority_score: a.priority_score,
          headline: a.headline,
        },
      })),
  }
}

interface LongPressMenuProps {
  x: number
  y: number
  alert: AlertSummary
  onClose: () => void
}

function LongPressMenu({ x, y, alert, onClose }: LongPressMenuProps) {
  const MENU_W = 184
  const menuLeft = Math.max(8, Math.min(x - MENU_W / 2, window.innerWidth - MENU_W - 8))
  const menuTop = Math.max(8, Math.min(y - 108, window.innerHeight - 120))

  const handleCopyCoords = () => {
    if (alert.centroid) {
      const [lon, lat] = alert.centroid.coordinates
      navigator.clipboard.writeText(`${lat.toFixed(5)}, ${lon.toFixed(5)}`)
    }
    onClose()
  }

  const handleShare = () => {
    const title = `CAP Alert: ${alert.headline ?? alert.hazard_type}`
    const text = alert.region_name ? `${title} — ${alert.region_name}` : title
    if (navigator.share) {
      navigator.share({ title, text, url: window.location.href }).catch(() => {})
    } else {
      navigator.clipboard.writeText(`${text}\n${window.location.href}`)
    }
    onClose()
  }

  return (
    <div
      style={{ position: 'fixed', top: menuTop, left: menuLeft, width: MENU_W, zIndex: 200 }}
      className="bg-white border border-gray-200 dark:bg-gray-800 dark:border-gray-700 rounded-xl shadow-2xl py-1 overflow-hidden"
    >
      <p className="px-3 pt-1 pb-0.5 text-xs text-gray-500 truncate">
        {alert.headline ?? alert.hazard_type}
      </p>
      <button
        type="button"
        onClick={handleCopyCoords}
        disabled={!alert.centroid}
        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-800 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700 disabled:opacity-40 transition-colors"
      >
        <svg className="w-4 h-4 text-gray-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
        </svg>
        Copy coordinates
      </button>
      <button
        type="button"
        onClick={handleShare}
        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-800 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700 transition-colors"
      >
        <svg className="w-4 h-4 text-gray-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/>
        </svg>
        Share alert
      </button>
    </div>
  )
}

export default function AlertLayer() {
  const { mapRef, mapReady } = useMapInstance()
  const { selectAlert } = useAlertStore()
  const alerts = useAlertStore(selectFilteredAlerts)
  const selectedId = useAlertStore((s) => s.selectedAlertId)
  const { data: selectedGeoJSON } = useAlertGeoJSON(selectedId)

  // Seed store with ALL active alerts so the map is never limited by list pagination
  useMapAlerts()

  const alertsRef = useRef(alerts)
  alertsRef.current = alerts
  const selectedIdRef = useRef(selectedId)
  selectedIdRef.current = selectedId

  // Long-press state
  const [longPressMenu, setLongPressMenu] = useState<{
    x: number; y: number; alert: AlertSummary
  } | null>(null)
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const longPressActiveRef = useRef(false)

  // Dismiss menu when tapping outside it
  useEffect(() => {
    if (!longPressMenu) return
    const handler = () => setLongPressMenu(null)
    const t = setTimeout(() => document.addEventListener('pointerdown', handler, { once: true }), 10)
    return () => { clearTimeout(t); document.removeEventListener('pointerdown', handler) }
  }, [longPressMenu])

  // Single effect: sets up layers on mount and re-adds them after every setStyle() call.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    let cancelled = false
    let listenersActive = false

    const longPressActiveRef_inner = longPressActiveRef

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleClick = (e: MapMouseEvent & { features?: any[] }) => {
      if (longPressActiveRef_inner.current) { longPressActiveRef_inner.current = false; return }
      const id = e.features?.[0]?.properties?.id as string | undefined
      if (id) selectAlert(id)
    }
    const handleMouseEnter = () => { map.getCanvas().style.cursor = 'pointer' }
    const handleMouseLeave = () => { map.getCanvas().style.cursor = '' }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleLongPressStart = (e: any) => {
      const feature = e.features?.[0]
      if (!feature) return
      const id = feature.properties.id as string
      const alertData = alertsRef.current.find(a => a.id === id)
      if (!alertData?.centroid) return
      const touch = e.originalEvent?.touches?.[0]
      if (!touch) return
      const { clientX: x, clientY: y } = touch
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = setTimeout(() => {
        longPressTimerRef.current = null
        longPressActiveRef_inner.current = true
        setLongPressMenu({ x, y, alert: alertData })
      }, 500)
    }

    const cancelLongPress = () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current)
        longPressTimerRef.current = null
      }
    }

    const addLayersAndData = () => {
      // Remove stale listeners before re-registering (avoids duplicates on style reload)
      if (listenersActive) {
        try {
          map.off('click', ICON_LAYER, handleClick)
          map.off('mouseenter', ICON_LAYER, handleMouseEnter)
          map.off('mouseleave', ICON_LAYER, handleMouseLeave)
          map.off('touchstart', ICON_LAYER, handleLongPressStart)
          map.off('touchend', ICON_LAYER, cancelLongPress)
          map.off('touchcancel', ICON_LAYER, cancelLongPress)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ;(map as any).off('touchmove', cancelLongPress)
        } catch { /* layer may not exist */ }
        listenersActive = false
      }

      if (!map.getSource(SOURCE_ID)) {
        map.addSource(SOURCE_ID, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        })
        // Ring renders behind the icon; filter is set by the selection effect
        map.addLayer({
          id: ICON_RING,
          type: 'circle',
          source: SOURCE_ID,
          filter: ['==', ['get', 'id'], selectedIdRef.current ?? ''],
          paint: {
            'circle-radius': 26,
            'circle-color': '#ffffff',
            'circle-opacity': 0.2,
            'circle-stroke-width': 2.5,
            'circle-stroke-color': '#ffffff',
            'circle-stroke-opacity': 0.9,
          },
        })
        map.addLayer({
          id: ICON_LAYER,
          type: 'symbol',
          source: SOURCE_ID,
          layout: {
            'icon-image': ['concat', 'cap-icon-', ['get', 'hazard_type']],
            'icon-size': selectedIdRef.current
              ? selectedSizeExpr(selectedIdRef.current)
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              : NORMAL_SIZE as any,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            'symbol-sort-key': sortKeyExpr(selectedIdRef.current) as any,
            'icon-allow-overlap': false,
            'icon-ignore-placement': false,
            'icon-anchor': 'center',
          },
        })
      }

      if (!map.getSource(SELECTED_SOURCE)) {
        map.addSource(SELECTED_SOURCE, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        })
        map.addLayer({
          id: SELECTED_FILL,
          type: 'fill',
          source: SELECTED_SOURCE,
          paint: { 'fill-color': '#ffffff', 'fill-opacity': 0.15 },
        })
        map.addLayer({
          id: SELECTED_OUTLINE,
          type: 'line',
          source: SELECTED_SOURCE,
          paint: { 'line-color': '#ffffff', 'line-width': 1.5, 'line-opacity': 0.7 },
        })
      }

      // Populate with current alerts
      const src = map.getSource(SOURCE_ID) as GeoJSONSource | undefined
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      src?.setData(buildCentroidGeoJSON(alertsRef.current) as any)

      map.on('click', ICON_LAYER, handleClick)
      map.on('mouseenter', ICON_LAYER, handleMouseEnter)
      map.on('mouseleave', ICON_LAYER, handleMouseLeave)
      map.on('touchstart', ICON_LAYER, handleLongPressStart)
      map.on('touchend', ICON_LAYER, cancelLongPress)
      map.on('touchcancel', ICON_LAYER, cancelLongPress)
      // Map-level touchmove cancels long-press when user pans the map
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(map as any).on('touchmove', cancelLongPress)
      listenersActive = true
    }

    const setup = async () => {
      if (cancelled) return
      addLayersAndData()
      await loadMapIcons(map)
    }

    // Re-run setup after every style change (setStyle wipes all sources + layers).
    const onStyleLoad = async () => {
      if (cancelled) return
      addLayersAndData()
      await loadMapIcons(map)
    }
    map.on('style.load', onStyleLoad)

    if (map.isStyleLoaded()) {
      setup()
    } else {
      map.once('load', setup)
    }

    return () => {
      cancelled = true
      map.off('style.load', onStyleLoad)
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current)
        longPressTimerRef.current = null
      }
      try {
        map.off('click', ICON_LAYER, handleClick)
        map.off('mouseenter', ICON_LAYER, handleMouseEnter)
        map.off('mouseleave', ICON_LAYER, handleMouseLeave)
        map.off('touchstart', ICON_LAYER, handleLongPressStart)
        map.off('touchend', ICON_LAYER, cancelLongPress)
        map.off('touchcancel', ICON_LAYER, cancelLongPress)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(map as any).off('touchmove', cancelLongPress)
      } catch { /* map may already be removed */ }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady])

  // Update ring highlight and icon size when selection changes
  useEffect(() => {
    const map = mapRef.current
    if (!map || !map.getLayer(ICON_RING)) return
    map.setFilter(ICON_RING, ['==', ['get', 'id'], selectedId ?? ''])
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    map.setLayoutProperty(ICON_LAYER, 'icon-size', selectedId ? selectedSizeExpr(selectedId) : NORMAL_SIZE as any)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    map.setLayoutProperty(ICON_LAYER, 'symbol-sort-key', sortKeyExpr(selectedId) as any)
  }, [selectedId, mapRef])

  // Sync centroid data when alerts change
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const source = map.getSource(SOURCE_ID) as GeoJSONSource | undefined
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    source?.setData(buildCentroidGeoJSON(alerts) as any)
  }, [alerts, mapRef])

  // Sync selected polygon outline — deliberately does NOT auto-fit/zoom the map camera.
  // Selecting an alert (from the list, or a map pin) should only show its outline in place;
  // zooming is a separate, explicit action (RegionFilter state buttons, AlertDetail's "View on
  // Map" button, both of which still call fitBounds themselves).
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const source = map.getSource(SELECTED_SOURCE) as GeoJSONSource | undefined
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    source?.setData((selectedGeoJSON ?? { type: 'FeatureCollection', features: [] }) as any)
  }, [selectedGeoJSON, mapRef])

  return longPressMenu
    ? createPortal(
        <LongPressMenu
          x={longPressMenu.x}
          y={longPressMenu.y}
          alert={longPressMenu.alert}
          onClose={() => setLongPressMenu(null)}
        />,
        document.body
      )
    : null
}
